import { useEffect, useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useTour } from '@reactour/tour'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { Alert, Text, Box } from '@machobear/design-system'
import { useGetUsage } from '@/hooks/use-block-and-pay'
import AreaTitle from '@/components/area-title'
import Panel from '@/components/panel'
import { FieldDropdown } from '@/components/form'
import { Input } from '@/components/form'
import isElementInView from '@/utils/is-element-in-view'
import { RocketIcon } from '@/components/icons'
import { useGetProduct } from '@/hooks/use-sponsored-listing'
import { breakpoints, colors, fontWeights } from '@/styles'
import Skeleton from '@/components/skeleton'
import { pushToDatalayer } from '@/utils/push-datalayer'

const StyledPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 30px 15px;
  }
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ColDropDown = styled(Col)`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 40px;
  }
`

const Row = styled.div`
  display: flex;
`

const StyledBox = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const StyledDropdownRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 14px;
  color: ${colors.grey24};
  margin: -8px -12px;
  padding: 8px 12px;
  padding-right: 25px;
`

const CheckBox = styled(Input)`
  margin-right: 15px;
  flex-shrink: 0;
`

const Link = styled.a`
  margin-left: 5px;
  color: ${colors.link};
  font-weight: ${fontWeights.regular};
  display: inline;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 0;
  }
`
const Paragraph = styled.p`
  display: inline;
  margin: 0;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

const FieldDropdownStyle = styled(FieldDropdown)`
  width: 100%;
  max-width: 230px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: 271px;
  }
`

const SponsoredListing = ({ setIsBoostYourListing, setBoostYourListingData }) => {
  const formik = useFormikContext()
  const { t } = useTranslation()
  const { listingId } = useParams()

  const [productPackage, setproductPackage] = useState()
  const [duration, setDuration] = useState()
  const [isChecked, setIsChecked] = useState(false)

  const { data: sevenDaysProducts, isLoading: sevenDaysProductsLoading } = useGetProduct(
    'seven-days-sponsored-listing-package'
  )
  const { data: fourteenDaysProducts, isLoading: fourteenDaysProductsLoading } = useGetProduct(
    'fourteen-days-sponsored-listing-package'
  )
  const { data: thirtyDaysProducts, isLoading: thirtyDaysProductsLoading } = useGetProduct(
    'thirty-days-sponsored-listing-package'
  )
  const { data: usage } = useGetUsage('sponsored_listing_days')

  const getProduct = p => {
    let products
    switch (p) {
      case 'seven-days-sponsored-listing-package':
        products = sevenDaysProducts
        break
      case 'fourteen-days-sponsored-listing-package':
        products = fourteenDaysProducts
        break
      case 'thirty-days-sponsored-listing-package':
        products = thirtyDaysProducts
        break
      default:
        products = sevenDaysProducts
    }

    return products?.products.filter(product => product.plan_name === p)[0]
  }
  const sevenDaysProduct = sevenDaysProducts?.products?.[0]
  const fourteenDaysProduct = fourteenDaysProducts?.products?.[0]
  const thirtyDaysProduct = thirtyDaysProducts?.products?.[0]
  const isLoading =
    sevenDaysProductsLoading || fourteenDaysProductsLoading || thirtyDaysProductsLoading
  const getDailyPrice = (p, duration) => {
    return Math.ceil((p / duration) * 100) / 100
  }

  const getDuration = value => {
    switch (value) {
      case 'seven-days-sponsored-listing-package':
        return '7'
      case 'fourteen-days-sponsored-listing-package':
        return '14'
      case 'thirty-days-sponsored-listing-package':
        return '28'
      default:
        return '7'
    }
  }

  const getEventId = value => {
    switch (value) {
      case 'seven-days-sponsored-listing-package':
        return 'application-create-sponsor-listing-modal-7day-pkg'
      case 'fourteen-days-sponsored-listing-package':
        return 'application-create-sponsor-listing-modal-14day-pkg'
      case 'thirty-days-sponsored-listing-package':
        return 'application-create-sponsor-listing-modal-30day-pkg'
      default:
        return null
    }
  }

  const formatOptionLabel = ({ dailyPrice, label, price }) => (
    <StyledDropdownRow>
      <div style={{ fontWeight: 400 }}>
        {label}
        <span style={{ color: colors.green40, marginLeft: '5px' }}>{dailyPrice}</span>
      </div>
      <div style={{ fontWeight: 600 }}>{price}</div>
    </StyledDropdownRow>
  )

  const DurationOptions = [
    {
      value: 'seven-days-sponsored-listing-package',
      label: t('l.listing.sponsored_listing.duration.week', '7 Days'),
      dailyPrice: `($${getDailyPrice(sevenDaysProduct?.regular_plan_price, 7)}/day)`,
      price: `$${sevenDaysProduct?.regular_plan_price}`
    },
    {
      value: 'fourteen-days-sponsored-listing-package',
      label: t('l.listing.sponsored_listing.duration.two_weeks', '14 Days'),
      dailyPrice: `($${getDailyPrice(fourteenDaysProduct?.regular_plan_price, 14)}/day)`,
      price: `$${fourteenDaysProduct?.regular_plan_price}`
    },
    {
      value: 'thirty-days-sponsored-listing-package',
      label: t('l.listing.sponsored_listing.duration.month2', '28 Days'),
      dailyPrice: `($${getDailyPrice(thirtyDaysProduct?.regular_plan_price, 28)}/day)`,
      price: `$${thirtyDaysProduct?.regular_plan_price}`
    }
  ]

  useEffect(() => {
    setBoostYourListingData({
      listingId: listingId,
      duration: productPackage,
      product: getProduct(productPackage)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPackage])

  const { setIsOpen, isOpen, setCurrentStep } = useTour()

  const toggleTour = step => {
    setCurrentStep(step)
    pushToDatalayer({ tourId: 'sllp-react' })
    setIsOpen(true)
  }

  const refElement = useRef(null)
  const isCreate = window.location.href?.includes('create')

  const purchased = usage?.blocks?.sponsored_listing_days?.value
  const used = usage?.blocks?.sponsored_listing_days?.usage?.current_value

  const count = purchased - used

  const selectedCount = getDuration(productPackage ? productPackage : '')

  // Adds scroll listener to trigger boost listing tour section
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('sponsored-tour'))

    const scrollTourSponsored = () => {
      clearTimeout(isScrolling)

      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const scrolledToBottom = window.scrollY + windowHeight >= documentHeight

      isScrolling = setTimeout(function () {
        if (
          isElementInView(refElement, { top: 50, bottom: 50, left: 0, right: 0 }) &&
          storedData?.boostListing === false
        ) {
          toggleTour(2)
        } else if (storedData?.publishListing === false && scrolledToBottom) {
          toggleTour(8)
        }
      }, 400)
    }
    let isScrolling
    if (
      storedData &&
      isCreate &&
      (storedData?.boostListing === false || storedData?.publishListing === false)
    ) {
      window.addEventListener('scroll', scrollTourSponsored)
    }

    return () => {
      window.removeEventListener('scroll', scrollTourSponsored)
    }
  }, [isOpen])

  // Pre-fill if user is in an active tour
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('sponsored-tour'))

    if (storedData && storedData?.boostListing === false && !isLoading && isCreate) {
      // If tour is active set duration to 7 days by default
      setDuration('7')
      setproductPackage('seven-days-sponsored-listing-package')
      formik.setFieldValue('duration', 'seven-days-sponsored-listing-package')

      // If tour is active set checkbox to checked by default
      setIsChecked(true)
      setIsBoostYourListing(true)
      // We should also fire the events related to these fields
      pushToDatalayer({
        event: 'application-create-sponsor-listing-click'
      })
      pushToDatalayer({
        event: 'application-create-sponsor-listing-modal-7day-pkg'
      })
    }
  }, [isLoading])

  const durationFooter = {
    HAS_ENOUGH_CREDITS: (
      <Alert tone={'positive'}>
        <Text size="small">
          <Trans
            i18nKey="l.listing.sponsored_listing.modal.duration_footer2"
            selectedCount={selectedCount}
          >
            {{ selectedCount }} days of credit will be automatically used on this listing.
          </Trans>
        </Text>
      </Alert>
    ),
    NOT_ENOUGH_CREDITS: (
      <Alert tone={'caution'}>
        <Text size="small">
          <Trans i18nKey="l.listing.sponsored_listing.modal.duration_footer3" count={count}>
            Your account only has enough credits for {{ count }} days. Payment is required to
            continue.
          </Trans>
        </Text>
      </Alert>
    )
  }

  const renderDurationFooter = duration => {
    if (duration === undefined) {
      return
    } else if (count >= parseInt(duration)) {
      return durationFooter.HAS_ENOUGH_CREDITS
    } else {
      return durationFooter.NOT_ENOUGH_CREDITS
    }
  }

  return (
    <div ref={refElement} className="create-sponsored">
      <AreaTitle>{t('l.listing.edit.sponsored_listing.subtitle', 'Boost your listing')}</AreaTitle>
      <StyledPanel vSpace={30} hSpace={30}>
        <StyledBox>
          <Col>
            <Row>
              <CheckBox
                type="checkbox"
                checked={isChecked}
                onChange={e => {
                  setIsBoostYourListing(e.target.checked)
                  setIsChecked(!isChecked)
                  formik.setFieldValue('isBoostYourListing', !isChecked)

                  // If the checkbox is checked, send an event to dataLayer
                  if (e.target.checked) {
                    pushToDatalayer({
                      event: 'application-create-sponsor-listing-click'
                    })
                  }
                }}
              />

              <Col style={{ gap: '4px', width: '354px' }}>
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {t('l.listing.boost.sponsored_listing_title', 'Sponsor your listing')}{' '}
                  <RocketIcon />
                </span>
                <span style={{ fontSize: '14px' }}>
                  <Paragraph>
                    {t(
                      'l.listing.boost.sponsored_listing_text',
                      'Get up to 300% more inquiries and boost your listing to the top of the results.'
                    )}
                  </Paragraph>
                  <Link
                    href="https://liv.rent/blog/livrent/how-sponsored-listings-fill-vacancies-faster/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('l.application.view.learn_more.button', 'Learn more')}
                  </Link>
                </span>
              </Col>
            </Row>
          </Col>

          <ColDropDown>
            {isLoading ? (
              <Skeleton height={52} />
            ) : (
              <FieldDropdownStyle
                name="duration"
                options={DurationOptions}
                onChange={value => {
                  const dur = getDuration(value)
                  setDuration(dur)
                  setproductPackage(value)
                  formik.setFieldValue('duration', value)
                  const eventId = getEventId(value)
                  pushToDatalayer({
                    event: eventId
                  })
                }}
                isDisabled={false}
                required={isChecked}
                placeholder={t('l.listing.sponsored_listing.duration.text', 'Duration')}
                formatOptionLabel={formatOptionLabel}
              />
            )}
          </ColDropDown>
        </StyledBox>
        <Box>{renderDurationFooter(duration)}</Box>
      </StyledPanel>
    </div>
  )
}

export default SponsoredListing
